import {
  getTokensByType,
  inspectFilterBranchesInSearchQuery,
  replaceAllFiltersByTypeInSearchQuery,
  replaceFilterBranchInSearchQuery,
  replaceTokensDifferentially,
  tokenizeQuery,
  type TokenType,
} from '@github-ui/filter-query'

function hasGroupedNoLabel(query: string) {
  const inspection = inspectFilterBranchesInSearchQuery(query, ['label'])
  return (
    inspection.isSafe &&
    inspection.hasBooleanStructure &&
    inspection.branches.some(branch => !branch.isNegated && branch.key === 'no')
  )
}

export function getRepoPullsFilterState(
  query: string,
  matchingTypes: readonly TokenType[],
  structuralUpdatesEnabled: boolean,
): string | undefined {
  if (!structuralUpdatesEnabled && !(matchingTypes.includes('label') && hasGroupedNoLabel(query))) {
    if (query.length > 1000) return undefined
    return tokenizeQuery(query)
      .filter(
        segment =>
          segment.type === 'filter' &&
          (matchingTypes.includes(segment.key.toLowerCase() as TokenType) ||
            (segment.key.toLowerCase() === 'no' &&
              segment.values.some(value => matchingTypes.includes(value.toLowerCase() as TokenType)))),
      )
      .flatMap(segment =>
        segment.type === 'filter'
          ? segment.values.map(value => `${segment.isNegated ? '-' : ''}${segment.key.toLowerCase()}:${value}`)
          : [],
      )
      .toSorted()
      .join(' ')
  }

  const inspection = inspectFilterBranchesInSearchQuery(query, matchingTypes)
  if (!inspection.isSafe) return undefined

  return inspection.branches
    .map(branch => JSON.stringify([branch.isNegated, branch.key, branch.values]))
    .toSorted()
    .join('\n')
}

export function isRepoPullsNoValueSelected(query: string, type: TokenType, structuralUpdatesEnabled: boolean) {
  if (type === 'label' && hasGroupedNoLabel(query)) return true
  if (!structuralUpdatesEnabled) {
    return tokenizeQuery(query).some(
      segment =>
        segment.type === 'filter' &&
        !segment.isNegated &&
        segment.key.toLowerCase() === 'no' &&
        segment.values.length === 1 &&
        segment.values[0]?.toLowerCase() === type,
    )
  }

  const inspection = inspectFilterBranchesInSearchQuery(query, [type])
  return inspection.isSafe && inspection.branches.some(branch => !branch.isNegated && branch.key === 'no')
}

export function getRepoPullsFilterPickerTokens(query: string, type: TokenType, structuralUpdatesEnabled: boolean) {
  if (!structuralUpdatesEnabled) return getTokensByType(query, type)

  const inspection = inspectFilterBranchesInSearchQuery(query, [type])
  if (!inspection.isSafe) return []
  if (
    inspection.branches.some(branch => branch.isNegated || branch.key !== type) ||
    (inspection.hasBooleanStructure && inspection.branches.length > 1)
  ) {
    return []
  }
  return inspection.branches.flatMap(branch => branch.values)
}

export function getRepoPullsPositiveFilterPickerTokens(
  query: string,
  type: TokenType,
  structuralUpdatesEnabled: boolean,
) {
  if (!structuralUpdatesEnabled) return getTokensByType(query, type)

  const inspection = inspectFilterBranchesInSearchQuery(query, [type])
  if (!inspection.isSafe) return []

  const tokens = inspection.branches
    .filter(branch => !branch.isNegated && branch.key === type)
    .flatMap(branch => branch.values)
  const identities = new Set(tokens.map(token => token.toLowerCase()))
  return identities.size === tokens.length ? tokens : []
}

export function replaceRepoPullsFilterInQuery(
  query: string,
  tokens: readonly string[],
  type: TokenType,
  structuralUpdatesEnabled: boolean,
  includeNoValue = false,
) {
  // Labels and "No labels" must be combined with OR, which needs the structural replacement.
  const combinesLabelsWithNoLabel = type === 'label' && (includeNoValue || hasGroupedNoLabel(query))
  if (!structuralUpdatesEnabled && !combinesLabelsWithNoLabel) {
    if (type === 'author' || type === 'assignee' || type === 'milestone') {
      return replaceAllFiltersByTypeInSearchQuery(
        query,
        includeNoValue ? [`no:${type}`] : [...tokens],
        type,
        includeNoValue,
      )
    }
    return replaceTokensDifferentially(query, [...tokens], type, {treatNoValueAsLiteral: type === 'label'})
  }

  return replaceFilterBranchInSearchQuery(query, tokens, type, {
    treatNoValueAsLiteral: type === 'label' || type === 'milestone',
    includeNoValue,
    replaceMultipleFlatBranches: true,
    reconcilePositiveBranchSubset: type === 'label',
  })
}
